import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Clock,
  DollarSign, Percent, TrendingUp, Settings, Plus, Edit2, 
  Archive, Play, Eye, ChevronDown, ChevronUp, Calculator,
  AlertTriangle, History, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../utils/apiConfig';

const RULE_TYPES = {
  platform_fee_tiers: { label: 'Platform Fee Tiers', icon: DollarSign, color: 'purple' },
  processing_fee: { label: 'Processing Fees', icon: Percent, color: 'blue' },
  transaction_limits: { label: 'Transaction Limits', icon: TrendingUp, color: 'emerald' }
};

const STATUS_COLORS = {
  draft: 'bg-slate-700 text-slate-300',
  scheduled: 'bg-amber-900/50 text-amber-400',
  active: 'bg-emerald-900/50 text-emerald-400',
  superseded: 'bg-slate-600 text-slate-400',
  archived: 'bg-red-900/30 text-red-400'
};

export default function AdminPricingView() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPricing, setCurrentPricing] = useState(null);
  const [rules, setRules] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [expandedRule, setExpandedRule] = useState(null);
  const [calculatorAmount, setCalculatorAmount] = useState(50);
  const [calculatorResult, setCalculatorResult] = useState(null);

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchPricingData = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch current pricing
      const currentRes = await fetch(`${API_URL}/api/admin/pricing/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!currentRes.ok) {
        if (currentRes.status === 401 || currentRes.status === 403) {
          throw new Error('Session expired');
        }
        throw new Error('Failed to fetch pricing');
      }
      
      const currentData = await currentRes.json();
      setCurrentPricing(currentData);

      // Fetch rules
      const rulesRes = await fetch(`${API_URL}/api/admin/pricing/rules?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (rulesRes.ok) {
        const rulesData = await rulesRes.json();
        setRules(rulesData.rules || []);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      if (err.message === 'Session expired') {
        localStorage.removeItem('pecash_token');
        localStorage.removeItem('pecash_admin');
        setTimeout(() => navigate('/admin'), 2000);
      }
    }
  }, [navigate, isAdmin]);

  useEffect(() => {
    fetchPricingData();
  }, [fetchPricingData]);

  const handleActivateRule = async (ruleId) => {
    const token = getToken();
    
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/rules/${ruleId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to activate rule');
      }
      
      await fetchPricingData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleArchiveRule = async (ruleId) => {
    const token = getToken();
    const reason = prompt('Enter reason for archiving:');
    if (!reason) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/rules/${ruleId}/archive?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to archive rule');
      }
      
      await fetchPricingData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCalculate = async () => {
    const token = getToken();
    
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/calculate?amount_usd=${calculatorAmount}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Calculation failed');
      }
      
      const data = await res.json();
      setCalculatorResult(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  if (error && error === 'Session expired') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center bg-slate-800 rounded-2xl p-8 border border-red-800">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Expired</h2>
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-slate-500 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Pricing Engine</h1>
              <p className="text-sm text-slate-400">Configurable Fee & Limit Management</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowCalculator(true)}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="open-calculator"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calculator
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Rule
            </Button>
            <Button
              onClick={fetchPricingData}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="refresh-pricing"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && error !== 'Session expired' && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex justify-between items-center">
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setError('')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pilot Mode Banner */}
        {currentPricing?.pilot_mode && (
          <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4 mb-6">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              PILOT MODE - Pricing changes affect simulated transactions only.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'current', label: 'Current Pricing', icon: Settings },
            { id: 'rules', label: 'Pricing Rules', icon: History }
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Current Pricing Tab */}
        {activeTab === 'current' && currentPricing && (
          <div className="space-y-6">
            {/* Platform Fee Tiers */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-900/50 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Platform Fee Tiers</h3>
                    <p className="text-sm text-slate-400">
                      Source: {currentPricing.platform_fees.source}
                      {currentPricing.platform_fees.rule_id && (
                        <span className="ml-2 text-purple-400">
                          (Rule: {currentPricing.platform_fees.rule_id.slice(0, 8)}...)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                {currentPricing.platform_fees.effective_from && (
                  <span className="text-xs text-slate-500">
                    Effective: {formatDate(currentPricing.platform_fees.effective_from)}
                  </span>
                )}
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="text-left p-3 text-slate-400 font-medium">Amount Range</th>
                      <th className="text-right p-3 text-slate-400 font-medium">Fee</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {currentPricing.platform_fees.tiers.map((tier, idx) => (
                      <tr key={idx} className="hover:bg-slate-700/50">
                        <td className="p-3 text-white">
                          {formatCurrency(tier.min_amount)} - {formatCurrency(tier.max_amount)}
                        </td>
                        <td className="p-3 text-right text-emerald-400 font-medium">
                          {formatCurrency(tier.fee)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Processing Fees */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-900/50 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Processing Fees</h3>
                  <p className="text-sm text-slate-400">
                    Source: {currentPricing.processing_fees.source}
                    {' | Provider: '}{currentPricing.processing_fees.config.provider}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Percentage</p>
                  <p className="text-white text-xl font-bold">
                    {(currentPricing.processing_fees.config.percentage * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Fixed Fee</p>
                  <p className="text-white text-xl font-bold">
                    {formatCurrency(currentPricing.processing_fees.config.fixed)}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Formula</p>
                  <p className="text-slate-300 text-sm font-mono">
                    amount × {(currentPricing.processing_fees.config.percentage * 100).toFixed(1)}% + ${currentPricing.processing_fees.config.fixed}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Limits */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Transaction Limits</h3>
                  <p className="text-sm text-slate-400">
                    Source: {currentPricing.transaction_limits.source}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Minimum</p>
                  <p className="text-white text-xl font-bold">
                    {formatCurrency(currentPricing.transaction_limits.config.min_amount_usd)}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Maximum</p>
                  <p className="text-white text-xl font-bold">
                    {formatCurrency(currentPricing.transaction_limits.config.max_amount_usd)}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Daily Limit</p>
                  <p className="text-white text-xl font-bold">
                    {currentPricing.transaction_limits.config.daily_limit_usd 
                      ? formatCurrency(currentPricing.transaction_limits.config.daily_limit_usd)
                      : 'No limit'
                    }
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Monthly Limit</p>
                  <p className="text-white text-xl font-bold">
                    {currentPricing.transaction_limits.config.monthly_limit_usd 
                      ? formatCurrency(currentPricing.transaction_limits.config.monthly_limit_usd)
                      : 'No limit'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-4">
            {rules.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
                <Settings className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No custom pricing rules configured.</p>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Rule
                </Button>
              </div>
            ) : (
              rules.map((rule) => {
                const typeInfo = RULE_TYPES[rule.rule_type] || RULE_TYPES.platform_fee_tiers;
                const TypeIcon = typeInfo.icon;
                
                return (
                  <motion.div
                    key={rule.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden"
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-700/50"
                      onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg bg-${typeInfo.color}-900/50 flex items-center justify-center`}>
                            <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-400`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium">{rule.name}</p>
                              <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[rule.status]}`}>
                                {rule.status}
                              </span>
                            </div>
                            <p className="text-sm text-slate-400">
                              {typeInfo.label} • Effective: {formatDate(rule.effective_from)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {rule.status === 'draft' && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleActivateRule(rule.id); }}
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`activate-rule-${rule.id}`}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Activate
                            </Button>
                          )}
                          {rule.status === 'scheduled' && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleActivateRule(rule.id); }}
                              size="sm"
                              variant="outline"
                              className="border-amber-600 text-amber-400 hover:bg-amber-900/50"
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Force Activate
                            </Button>
                          )}
                          {['draft', 'scheduled'].includes(rule.status) && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleArchiveRule(rule.id); }}
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:bg-red-900/30"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                          {expandedRule === rule.id 
                            ? <ChevronUp className="w-5 h-5 text-slate-400" />
                            : <ChevronDown className="w-5 h-5 text-slate-400" />
                          }
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedRule === rule.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-slate-700 p-4 bg-slate-900/50"
                        >
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-slate-400">Rule ID</p>
                              <p className="text-white font-mono text-xs">{rule.id}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Version</p>
                              <p className="text-white">{rule.version}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Created</p>
                              <p className="text-white">{formatDate(rule.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-slate-400">Effective Until</p>
                              <p className="text-white">
                                {rule.effective_to ? formatDate(rule.effective_to) : 'Indefinite'}
                              </p>
                            </div>
                          </div>
                          
                          {rule.description && (
                            <div className="mb-4">
                              <p className="text-slate-400 text-sm">Description</p>
                              <p className="text-white">{rule.description}</p>
                            </div>
                          )}
                          
                          {/* Rule-specific details */}
                          {rule.fee_tiers && (
                            <div>
                              <p className="text-slate-400 text-sm mb-2">Fee Tiers</p>
                              <div className="bg-slate-800 rounded-lg p-3">
                                {rule.fee_tiers.map((tier, idx) => (
                                  <div key={idx} className="flex justify-between py-1">
                                    <span className="text-slate-300">
                                      ${tier.min_amount} - ${tier.max_amount}
                                    </span>
                                    <span className="text-emerald-400">${tier.fee}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {rule.processing_fee && (
                            <div>
                              <p className="text-slate-400 text-sm mb-2">Processing Fee Config</p>
                              <div className="bg-slate-800 rounded-lg p-3">
                                <p className="text-white">
                                  {(rule.processing_fee.percentage * 100).toFixed(1)}% + ${rule.processing_fee.fixed}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {rule.transaction_limits && (
                            <div>
                              <p className="text-slate-400 text-sm mb-2">Limits</p>
                              <div className="bg-slate-800 rounded-lg p-3 grid grid-cols-2 gap-2">
                                <div>
                                  <span className="text-slate-400">Min:</span>
                                  <span className="text-white ml-2">${rule.transaction_limits.min_amount_usd}</span>
                                </div>
                                <div>
                                  <span className="text-slate-400">Max:</span>
                                  <span className="text-white ml-2">${rule.transaction_limits.max_amount_usd}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Calculator Modal */}
      <AnimatePresence>
        {showCalculator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCalculator(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Price Calculator</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowCalculator(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-slate-400 text-sm block mb-2">Amount (USD)</label>
                  <input
                    type="number"
                    value={calculatorAmount}
                    onChange={(e) => setCalculatorAmount(parseFloat(e.target.value) || 0)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                    min="1"
                    max="1000"
                    data-testid="calculator-amount"
                  />
                </div>
                
                <Button 
                  onClick={handleCalculate}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="calculate-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
                
                {calculatorResult && (
                  <div className="bg-slate-900 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Base Amount</span>
                      <span className="text-white">{formatCurrency(calculatorResult.amount_usd)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Platform Fee</span>
                      <span className="text-amber-400">{formatCurrency(calculatorResult.platform_fee)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Processing Fee</span>
                      <span className="text-amber-400">{formatCurrency(calculatorResult.processing_fee)}</span>
                    </div>
                    <div className="border-t border-slate-700 pt-2 flex justify-between">
                      <span className="text-white font-medium">Total to Pay</span>
                      <span className="text-emerald-400 font-bold">{formatCurrency(calculatorResult.total_to_pay)}</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Rule Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateRuleModal 
            onClose={() => setShowCreateModal(false)} 
            onSuccess={() => { setShowCreateModal(false); fetchPricingData(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CreateRuleModal({ onClose, onSuccess }) {
  const [ruleType, setRuleType] = useState('platform_fee_tiers');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [feeTiers, setFeeTiers] = useState([
    { min_amount: 10, max_amount: 30, fee: 1.49 },
    { min_amount: 31, max_amount: 60, fee: 1.99 },
    { min_amount: 61, max_amount: 100, fee: 2.49 }
  ]);
  const [processingFee, setProcessingFee] = useState({ percentage: 0.029, fixed: 0.30, provider: 'stripe' });
  const [limits, setLimits] = useState({ min_amount_usd: 10, max_amount_usd: 100 });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    const token = localStorage.getItem('pecash_token');
    setCreating(true);
    setError('');
    
    try {
      const body = {
        rule_type: ruleType,
        name,
        description: description || null,
        effective_from: new Date(effectiveFrom).toISOString()
      };
      
      if (ruleType === 'platform_fee_tiers') {
        body.fee_tiers = feeTiers;
      } else if (ruleType === 'processing_fee') {
        body.processing_fee = processingFee;
      } else if (ruleType === 'transaction_limits') {
        body.transaction_limits = limits;
      }
      
      const res = await fetch(`${API_URL}/api/admin/pricing/rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create rule');
      }
      
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...feeTiers];
    newTiers[index] = { ...newTiers[index], [field]: parseFloat(value) || 0 };
    setFeeTiers(newTiers);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create Pricing Rule</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {/* Rule Type */}
          <div>
            <label className="text-slate-400 text-sm block mb-2">Rule Type</label>
            <select
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              data-testid="rule-type-select"
            >
              <option value="platform_fee_tiers">Platform Fee Tiers</option>
              <option value="processing_fee">Processing Fee</option>
              <option value="transaction_limits">Transaction Limits</option>
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="text-slate-400 text-sm block mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              placeholder="e.g., Q4 2026 Fee Structure"
              data-testid="rule-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-slate-400 text-sm block mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              rows={2}
              placeholder="Optional description..."
            />
          </div>

          {/* Effective From */}
          <div>
            <label className="text-slate-400 text-sm block mb-2">Effective From</label>
            <input
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              data-testid="effective-from-input"
            />
          </div>

          {/* Rule-specific config */}
          {ruleType === 'platform_fee_tiers' && (
            <div>
              <label className="text-slate-400 text-sm block mb-2">Fee Tiers</label>
              <div className="space-y-2">
                {feeTiers.map((tier, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="number"
                      value={tier.min_amount}
                      onChange={(e) => updateTier(idx, 'min_amount', e.target.value)}
                      className="w-20 bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"
                      placeholder="Min"
                    />
                    <span className="text-slate-500">-</span>
                    <input
                      type="number"
                      value={tier.max_amount}
                      onChange={(e) => updateTier(idx, 'max_amount', e.target.value)}
                      className="w-20 bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"
                      placeholder="Max"
                    />
                    <span className="text-slate-500">=</span>
                    <div className="flex items-center">
                      <span className="text-slate-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={tier.fee}
                        onChange={(e) => updateTier(idx, 'fee', e.target.value)}
                        className="w-16 bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"
                        placeholder="Fee"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ruleType === 'processing_fee' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm block mb-2">Percentage</label>
                <input
                  type="number"
                  step="0.001"
                  value={processingFee.percentage}
                  onChange={(e) => setProcessingFee({...processingFee, percentage: parseFloat(e.target.value)})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm block mb-2">Fixed ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={processingFee.fixed}
                  onChange={(e) => setProcessingFee({...processingFee, fixed: parseFloat(e.target.value)})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                />
              </div>
            </div>
          )}

          {ruleType === 'transaction_limits' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm block mb-2">Min Amount ($)</label>
                <input
                  type="number"
                  value={limits.min_amount_usd}
                  onChange={(e) => setLimits({...limits, min_amount_usd: parseFloat(e.target.value)})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm block mb-2">Max Amount ($)</label>
                <input
                  type="number"
                  value={limits.max_amount_usd}
                  onChange={(e) => setLimits({...limits, max_amount_usd: parseFloat(e.target.value)})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                />
              </div>
            </div>
          )}

          <div className="pt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !name}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="create-rule-submit"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Rule
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
